import Hls from "hls.js";
import { useEffect, useRef } from "react";

interface ChatVideoAttachmentProps {
  /**
   * URL used for playback. May be:
   *   - A `blob:` URL pointing at the local File (instant in-session preview)
   *   - An HLS `.m3u8` playlist (same URL the ticket's VideoMode plays)
   *   - A raw MP4/MOV URL (last-resort fallback)
   */
  src: string;
  name?: string;
}

/**
 * Video rendering for attachments inside a chat bubble. Intentionally mirrors
 * the ticket's `VideoMode` playback setup: set `<video src>` directly and, if
 * hls.js is supported, also attach it so Chrome/Firefox can play m3u8 streams.
 * Safari plays m3u8 natively via the `src` attribute; every other browser gets
 * it via `hls.attachMedia`. No retries, spinners, or error-state UI — the
 * native `<video controls>` already provides loading/error affordances and the
 * ticket's player has been shipping this exact pattern without the failure
 * modes we saw in the chat's custom `useHlsPlayer` wrapper.
 */
export function ChatVideoAttachment({ src, name }: ChatVideoAttachmentProps) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement || !src) return;

    const isHlsManifest = /\.m3u8(\?|$)/i.test(src);
    if (!isHlsManifest || !Hls.isSupported()) {
      // Native playback: blob URLs, MP4/MOV, and Safari's built-in HLS.
      return;
    }

    const hls = new Hls();
    hls.loadSource(src);
    hls.attachMedia(videoElement);
    return () => {
      hls.destroy();
    };
  }, [src]);

  return (
    // eslint-disable-next-line jsx-a11y/media-has-caption
    <video
      ref={videoRef}
      src={src}
      controls
      playsInline
      preload="metadata"
      aria-label={name}
      className="block max-h-48 max-w-full rounded-lg bg-puntt-neutral-gray-3"
      data-testid="chat-video-attachment"
    />
  );
}
